import { useEffect, useState } from 'react';
import { Save } from '../../lib/icons';
import { toast } from 'sonner';
import { fetchFinances, majSoldesOuverture } from '../../lib/dataProvider';
import { fmt } from '../shared/badges';

const inputCls =
  'h-10 w-full border border-slate-300 bg-slate-50 px-2 font-data text-sm text-slate-900 outline-none transition-colors duration-150 focus:border-slate-900 focus:bg-white';
const th = 'px-3 py-2 text-left text-[11px] font-bold uppercase tracking-[0.08em] text-slate-500';

// Report d'ouverture de la nouvelle saison : solde de départ par compte (découverts inclus).
export const SoldesOuverture = () => {
  const [lignes, setLignes] = useState([]);
  const [soldes, setSoldes] = useState([]);

  const load = async () => {
    const fin = await fetchFinances();
    setSoldes(fin.soldes);
    setLignes(
      fin.comptes.map((c) => ({
        id: c.id,
        nom: c.nom,
        type: c.type,
        initial: String(c.initial ?? 0),
        dateOuverture: c.dateOuverture ?? '',
        noteOuverture: c.noteOuverture ?? '',
        decouvertAutorise: Boolean(c.decouvertAutorise),
      }))
    );
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const maj = (id, champ, valeur) =>
    setLignes((ls) => ls.map((l) => (l.id === id ? { ...l, [champ]: valeur } : l)));

  const enregistrer = async () => {
    const { error } = await majSoldesOuverture(lignes);
    if (error) return toast.error(error);
    toast.success('Soldes d’ouverture enregistrés — les soldes de trésorerie sont recalculés');
    load();
  };

  return (
    <section data-testid="soldes-ouverture" className="border border-slate-200 border-l-2 border-l-violet-600 bg-white">
      <div className="border-b border-slate-200 px-4 py-3">
        <p className="font-heading text-base font-bold tracking-tight text-slate-900">Soldes d’ouverture de trésorerie</p>
        <p className="text-xs text-slate-500">
          Report de début de saison pour chaque caisse et compte bancaire. Les montants <b>négatifs sont acceptés</b> pour
          refléter un découvert. Seules les opérations datées à partir de la date d’ouverture entrent dans le solde de la
          nouvelle saison.
        </p>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse">
          <thead className="bg-slate-50">
            <tr>{['Compte', 'Solde d’ouverture (TND)', 'Date d’ouverture', 'Découvert autorisé', 'Note', 'Solde actuel'].map((c) => <th key={c} className={th}>{c}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lignes.map((l) => {
              const actuel = soldes.find((s) => s.id === l.id)?.solde ?? 0;
              return (
                <tr key={l.id} data-testid={`ouverture-${l.id}`}>
                  <td className="px-3 py-2">
                    <p className="text-sm font-bold text-slate-900">{l.nom}</p>
                    <p className="font-data text-[11px] uppercase tracking-[0.08em] text-slate-500">{l.type}</p>
                  </td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`ouverture-initial-${l.id}`}
                      type="number"
                      step="0.001"
                      value={l.initial}
                      onChange={(e) => maj(l.id, 'initial', e.target.value)}
                      className={`${inputCls} tabular text-right ${Number(l.initial) < 0 ? 'text-red-700' : ''}`}
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`ouverture-date-${l.id}`}
                      type="date"
                      value={l.dateOuverture}
                      onChange={(e) => maj(l.id, 'dateOuverture', e.target.value)}
                      className={inputCls}
                    />
                  </td>
                  <td className="px-3 py-2 text-center">
                    <input
                      data-testid={`ouverture-decouvert-${l.id}`}
                      type="checkbox"
                      checked={l.decouvertAutorise}
                      onChange={(e) => maj(l.id, 'decouvertAutorise', e.target.checked)}
                      className="h-4 w-4 accent-slate-900"
                    />
                  </td>
                  <td className="px-3 py-2">
                    <input
                      data-testid={`ouverture-note-${l.id}`}
                      value={l.noteOuverture}
                      onChange={(e) => maj(l.id, 'noteOuverture', e.target.value)}
                      placeholder="ex. découvert autorisé 50 000 TND"
                      className={inputCls}
                    />
                  </td>
                  <td data-testid={`ouverture-solde-actuel-${l.id}`} className={`tabular px-3 py-2 text-right font-data text-sm font-semibold ${actuel < 0 ? 'text-red-700' : 'text-slate-900'}`}>
                    {fmt(actuel, 3)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="flex justify-end border-t border-slate-200 px-4 py-3">
        <button
          type="button"
          data-testid="enregistrer-soldes-ouverture"
          onClick={enregistrer}
          className="flex h-10 items-center gap-2 bg-slate-900 px-5 font-heading text-sm font-bold text-white transition-colors duration-150 hover:bg-slate-700"
        >
          <Save size={15} /> Enregistrer les soldes d’ouverture
        </button>
      </div>
    </section>
  );
};
